/**
 * Plugin manifest (ADR 0025): what a plugin is called, what it may do, and what
 * it adds to Nexus. Read defensively: it may come from an AI or an import.
 */

export type PluginEvent = 'task.completed' | 'page.created' | 'schedule';

export interface PluginSchedule {
  every: 'day' | 'weekdays' | 'week' | 'month';
  weekday: number;
  monthDay: number;
  time: string;
}

export interface PluginManifest {
  /** Slug, unique per workspace: "weekrapport". */
  id: string;
  name: string;
  version: string;
  description: string;
  permissions: {
    /** Read pages, tasks and databases. */
    read: boolean;
    /** Propose changes (each one is confirmed by the user). */
    write: boolean;
    /** Domains it may call over HTTPS, e.g. "api.wildix.com". */
    network: string[];
  };
  blocks: { id: string; title: string }[];
  commands: { id: string; title: string }[];
  views: { id: string; title: string; icon: string | null }[];
  automations: { id: string; title: string; on: PluginEvent; schedule: PluginSchedule | null }[];
}

const str = (v: unknown, max: number, fallback = '') =>
  typeof v === 'string' ? v.trim().slice(0, max) : fallback;
const slug = (v: unknown) =>
  str(v, 60)
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '');
const list = (v: unknown) => (Array.isArray(v) ? v.slice(0, 20) : []) as Record<string, unknown>[];

/** A host name only: no scheme, path, port or wildcard; never a private address. */
export function cleanDomain(v: unknown): string | null {
  const d = str(v, 253)
    .toLowerCase()
    .replace(/^https?:\/\//, '')
    .replace(/\/.*$/, '');
  if (!/^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(d)) return null;
  if (/(^|\.)(localhost|local|internal|lan)$/.test(d)) return null;
  return d;
}

function readSchedule(v: unknown): PluginSchedule | null {
  const s = (v && typeof v === 'object' ? v : null) as Record<string, unknown> | null;
  if (!s || !['day', 'weekdays', 'week', 'month'].includes(s.every as string)) return null;
  const int = (x: unknown, min: number, max: number, d: number) =>
    typeof x === 'number' && Number.isInteger(x) && x >= min && x <= max ? x : d;
  return {
    every: s.every as PluginSchedule['every'],
    weekday: int(s.weekday, 0, 6, 1),
    monthDay: int(s.monthDay, 1, 31, 1),
    time: typeof s.time === 'string' && /^\d{1,2}:\d{2}$/.test(s.time) ? s.time : '09:00',
  };
}

/** A valid manifest from anything, or an error message. */
export function readManifest(raw: unknown): { manifest: PluginManifest } | { error: string } {
  const v = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const id = slug(v.id);
  const name = str(v.name, 80);
  if (!id) return { error: 'manifest.id ontbreekt' };
  if (!name) return { error: 'manifest.name ontbreekt' };
  const p = (v.permissions && typeof v.permissions === 'object' ? v.permissions : {}) as Record<
    string,
    unknown
  >;
  const network = [
    ...new Set(
      list(p.network as unknown)
        .map((d) => cleanDomain(d))
        .filter((d): d is string => !!d),
    ),
  ];
  const items = (key: string) =>
    list(v[key])
      .map((x) => ({ id: slug(x?.id), title: str(x?.title, 80) }))
      .filter((x) => x.id && x.title);
  const manifest: PluginManifest = {
    id,
    name,
    version: str(v.version, 20, '1.0.0') || '1.0.0',
    description: str(v.description, 500),
    permissions: { read: p.read === true, write: p.write === true, network },
    blocks: items('blocks'),
    commands: items('commands'),
    views: list(v.views)
      .map((x) => ({ id: slug(x?.id), title: str(x?.title, 80), icon: str(x?.icon, 8) || null }))
      .filter((x) => x.id && x.title),
    automations: list(v.automations)
      .map((x) => {
        const on = (['task.completed', 'page.created', 'schedule'] as const).find(
          (e) => e === x?.on,
        );
        return on
          ? { id: slug(x?.id), title: str(x?.title, 80), on, schedule: readSchedule(x?.schedule) }
          : null;
      })
      .filter(
        (x): x is PluginManifest['automations'][number] =>
          !!x && !!x.id && !!x.title && (x.on !== 'schedule' || !!x.schedule),
      ),
  };
  if (
    !manifest.blocks.length &&
    !manifest.commands.length &&
    !manifest.views.length &&
    !manifest.automations.length
  )
    return { error: 'De plugin voegt niets toe (geen blocks, commands, views of automations).' };
  return { manifest };
}
